/**
 * 客戶頁「LINE 綁定」分頁(REQ-20260518002):
 * 列出此客戶被綁定的所有 LINE 聯絡人,顯示狀態 / 最後訊息 / 前往對話 / 解除綁定。
 */
import { Link } from 'react-router-dom';
import type { LineContactListItem, LineContactStatus } from '@app/shared';
import { useLineContactsByCustomer, useUnbindCustomer } from './lineApi';

interface Props {
  customerId: number;
}

export function CustomerLineContactsPanel({ customerId }: Props): JSX.Element {
  const { data, isLoading, isError, error } = useLineContactsByCustomer(customerId);
  const unbind = useUnbindCustomer();

  if (isLoading) return <div className="text-sm text-slate-500">載入中...</div>;
  if (isError) {
    return (
      <div role="alert" className="text-sm text-red-600">
        載入失敗:{error instanceof Error ? error.message : '未知錯誤'}
      </div>
    );
  }
  const items = data ?? [];
  if (items.length === 0) {
    return (
      <div className="text-sm text-slate-500 p-4 bg-slate-50 rounded">
        此客戶尚未被任何 LINE 聯絡人綁定。
        <br />
        到「AI客服」選擇對話 → 點對話標頭的「綁定客戶」即可建立關聯。
      </div>
    );
  }
  return (
    <div className="grid gap-2">
      {items.map((c) => (
        <ContactRow key={c.id} c={c} onUnbind={(id) => unbind.mutate(id)} unbinding={unbind.isPending} />
      ))}
    </div>
  );
}

function ContactRow({
  c,
  onUnbind,
  unbinding,
}: {
  c: LineContactListItem;
  onUnbind: (id: number) => void;
  unbinding: boolean;
}): JSX.Element {
  return (
    <div className="flex items-center gap-3 bg-white border rounded p-3">
      {c.pictureUrl ? (
        <img
          src={c.pictureUrl}
          alt=""
          className="w-10 h-10 rounded-full object-cover"
          referrerPolicy="no-referrer"
        />
      ) : (
        <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-sm text-slate-500">
          {(c.displayName ?? '?').slice(0, 1)}
        </div>
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-900 truncate">
            {c.displayName ?? c.lineUserId}
          </span>
          <StatusBadge status={c.status} />
          {c.assignedToName ? (
            <span className="text-[10px] text-slate-500">承辦:{c.assignedToName}</span>
          ) : null}
        </div>
        <div className="text-xs text-slate-500 truncate">
          {c.lastMessagePreview ?? '(無訊息)'}
          {c.lastMessageAt ? (
            <span className="ml-2 text-slate-400">
              · {new Date(c.lastMessageAt).toLocaleString('zh-TW')}
            </span>
          ) : null}
        </div>
      </div>

      <Link
        to={`/smart-service?contactId=${c.id}`}
        className="text-xs border border-blue-600 text-blue-700 px-2 py-1 rounded hover:bg-blue-50"
      >
        前往對話
      </Link>
      <button
        type="button"
        disabled={unbinding}
        onClick={() => {
          if (window.confirm(`確定解除「${c.displayName ?? c.lineUserId}」與此客戶的綁定?`)) {
            onUnbind(c.id);
          }
        }}
        className="text-xs border border-slate-400 text-slate-700 px-2 py-1 rounded hover:bg-slate-100 disabled:opacity-50"
      >
        {unbinding ? '解除中…' : '解除綁定'}
      </button>
    </div>
  );
}

function StatusBadge({ status }: { status: LineContactStatus }): JSX.Element {
  const cls =
    status === 'AI'
      ? 'bg-emerald-100 text-emerald-700'
      : status === 'HUMAN'
        ? 'bg-amber-100 text-amber-700'
        : 'bg-slate-200 text-slate-600';
  const label = status === 'AI' ? 'AI' : status === 'HUMAN' ? '人工' : '已關閉';
  return <span className={`text-[10px] px-1.5 py-0.5 rounded ${cls}`}>{label}</span>;
}
